import {
  WORKSPACE_AVATAR_CIRCLE_RING_RATIO,
  WORKSPACE_AVATAR_INITIALS_RATIO,
  WORKSPACE_AVATAR_RADIUS_RATIO,
  WORKSPACE_AVATAR_RING_RATIO,
  type WorkspaceAvatarShape,
  workspaceAvatarStyle,
  workspaceInitials,
} from '@wix/ai-scheduling-assistant-common-lib/entities';
import { wixImagePixelSize, wixScaledImageUrl } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { type WorkspaceSummary } from '../../lib/workspaces';
import styles from './WorkspaceBadge.module.css';

/**
 * A workspace's ("Space") badge — its uploaded icon when it has one, else the design's deterministic
 * initials tile (Figma "spaces nav", node `11400:41580`).
 *
 * The decision, in the order native makes it (`WorkspaceBadge` in iOS `DrawerOverlay.swift` /
 * Android `Components.kt`):
 *
 *   1. a non-empty `ws.icon`, else a non-empty `fallbackIcon` → the image, Wix-media-scaled to the
 *      rendered size and clipped to the silhouette
 *   2. else → the initials tile, in the style keyed on `id` (falling back to `name` when there is no id)
 *
 * The 5 background/ring/initials triples, the id hash and the ratios all come from common-lib's
 * `workspaceAvatar`, which both native ports mirror 1:1 — so the same workspace reads identically on
 * every surface and on all three platforms. **This is the only thing in the web app that draws a space
 * avatar; never hand-roll a second one.**
 *
 * Every measurement scales off `size` (radius `0.3×`, inside ring `0.08×`, initials `0.4×`), so the one
 * component serves every call site — the drawer's 16 through the page header's 42 — as native's single
 * scalable asset per style does. An icon'd workspace is clipped to the SAME silhouette, so a workspace
 * that gains or loses a photo keeps its shape.
 *
 * Takes only the identity fields it draws, so any caller holding a workspace-shaped row can render it.
 */
export function WorkspaceBadge({
  ws,
  size = 32,
  shape = 'roundedSquare',
  fallbackIcon,
}: {
  ws: Pick<WorkspaceSummary, 'id' | 'name' | 'icon'>;
  size?: number;
  /** `circle` only for the piled-up home component, which needs the agent dot's silhouette. */
  shape?: WorkspaceAvatarShape;
  /**
   * Used ONLY when `ws.icon` is empty — a caller-baked icon (a piled-up job carries its workspace's
   * icon on the row) that stands in until the workspace list has merged. Never overrides `ws.icon`.
   */
  fallbackIcon?: string;
}) {
  const circle = shape === 'circle';
  const borderRadius = circle ? '50%' : size * WORKSPACE_AVATAR_RADIUS_RATIO;
  const tile = { width: size, height: size, borderRadius };
  const icon = ws.icon || fallbackIcon;

  if (icon) {
    // A 32px tile must not download a full-size original. `devicePixelRatio` is read at render rather
    // than captured, and is absent during SSR — 1 is the safe floor, and the client render corrects it.
    const px = wixImagePixelSize(size, typeof window === 'undefined' ? 1 : window.devicePixelRatio);
    return (
      <img src={wixScaledImageUrl(icon, px, px, 'fill')} alt="" aria-hidden className={styles.icon} style={tile} />
    );
  }

  // The id is the stable key: a rename changes the name (and re-slugs the workspace), and only the id
  // holds still. `name` is the fallback native uses when a row carries no id at all.
  const style = workspaceAvatarStyle(ws.id || ws.name);
  return (
    <span
      aria-hidden
      className={styles.tile}
      style={{
        ...tile,
        borderWidth: size * (circle ? WORKSPACE_AVATAR_CIRCLE_RING_RATIO : WORKSPACE_AVATAR_RING_RATIO),
        fontSize: size * WORKSPACE_AVATAR_INITIALS_RATIO,
        background: style.background,
        borderColor: style.ring,
        color: style.initials,
      }}
    >
      {workspaceInitials(ws.name)}
    </span>
  );
}
